import React, { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import { ModuleRegistry } from "ag-grid-community";
import { AllCommunityModule, ClientSideRowModelModule, CsvExportModule } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { toast } from "react-toastify";
import moment from "moment";

// Components
import { Toast } from "../../../config/Toast";
import { ColumnCutRecap } from "./Column";
import { FormatSalaryItem } from "../../../helper/ResponseSalary";
import Pagination from "../../../components/Pagination";
import Button from "../../../components/Button";
import ActionRecap from "./action";
import Posting from "./modals/Posting";
import Detail from "./modals/Detail";
import ImageEvidance from "./modals/ImageEvidance";

// Services
import KasbonService from "../../../services/KasbonDeduction";
import BookmarkSalaryService from "../../../services/BookmarkSalary";

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule, CsvExportModule]);

function SalaryCutCashReceipt() {
  const [rowData, setRowData] = useState([]);
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [totalPage, setTotalPage] = useState(0);
  // const [dataId, setDataId] = useState(null);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [loadingPost, setLoadingPost] = useState(false);

  // Modal states
  const [modalPosting, setModalPosting] = useState(false);
  const [modalDetail, setModalDetail] = useState(false);
  const [modalImage, setModalImage] = useState(false);

  // Data states
  const [dataDetail, setDataDetail] = useState(null);
  const [image, setImage] = useState(null);
  const [payloadSort, setPayloadSort] = useState(null);
  const moduleBookmark = "kasbon";

  const limitDataPages = 10;

  const notifySuccess = useCallback((msg) => toast.success(msg, Toast), []);
  const notifyFailed = useCallback((msg) => toast.error(msg, Toast), []);

  const handleDateChange = useCallback((dates) => {
    if (dates?.length === 2) {
      setStartDate(moment(dates[0]).format("YYYY-MM-DD"));
      setEndDate(moment(dates[1]).format("YYYY-MM-DD"));
      setPageNumber(1);
    }
  }, []);

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPageNumber(1);
  }, []);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    const sort_by = payloadSort?.sort_by || null;
    const sort_type = payloadSort?.sort_type || null;

    try {
      const { data: res } = await KasbonService.list(
        pageNumber,
        limitDataPages,
        search,
        startDate,
        endDate,
        sort_by,
        sort_type
      );
      const { data = [], total_page = 0 } = res;

      const formattedData = FormatSalaryItem(data, pageNumber, limitDataPages);

      setRowData(formattedData);
      setTotalPage(total_page);
    } catch (err) {
      console.error(err);
      setRowData([]); setTotalPage(0);
      notifyFailed(err.response.data.message || "Gagal memuat data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, pageNumber, search, notifyFailed, payloadSort]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = useCallback(async () => {
    if (!startDate || !endDate) return notifyFailed("Periode rentang waktu tidak boleh kosong");

    setLoadingFilter(true);
    await fetchData();
    setLoadingFilter(false);
  }, [startDate, endDate, fetchData, notifyFailed]);

  const handOpenModalPost = useCallback(() => {
    if (!startDate || !endDate) {
      return notifyFailed("Periode rentang waktu tidak boleh kosong");
    }
    setModalPosting(true);
  }, [startDate, endDate, notifyFailed]);

  const handlePost = useCallback(async () => {
    if (!startDate || !endDate) return notifyFailed("Periode rentang waktu tidak boleh kosong");

    setLoadingPost(true);
    try {
      await KasbonService.post({ start_date: startDate, end_date: endDate });
      notifySuccess("Upah tukang berhasil direkap");
      fetchData();
    } catch {
      notifyFailed("Gagal posting data");
    } finally {
      setLoadingPost(false);
    }
  }, [startDate, endDate, notifySuccess, notifyFailed, fetchData]);

  const imageHandler = useCallback((imageUrl) => {
    setImage(imageUrl);
    setModalImage(true);
  }, []);

  const detailHandler = useCallback(
    (id, workerName, data) => {
      data.id = id;
      data.worker_name = workerName;
      setDataDetail(data);
      setModalDetail(true);
    },
    [setDataDetail, setModalDetail]
  );

  const handleToggleBookmark = useCallback(
    async (id, currentBookmark, bookMarkValue) => {
      setRowData((prev) =>
        prev.map((r) => {
          if (r.id === id) {
            const newBookmark = r.isBookmark === 1 ? 0 : 1;
            return { ...r, isBookmark: newBookmark };
          }
          return r;
        })
      );
      try {
        await BookmarkSalaryService.bookmark(id, bookMarkValue === 1 ? 0 : 1, moduleBookmark);
        notifySuccess("Berhasil memperbarui bookmark");
      } catch (err) {
        setRowData((prev) => prev.map((r) => (r.id === id ? { ...r, bookmark: currentBookmark } : r)));
        console.error("confirm bookmark error:", err);
        notifyFailed("Gagal memperbarui bookmark");
      } finally {
        fetchData({ page: pageNumber });
      }
    },
    [notifyFailed, notifySuccess, fetchData, pageNumber]
  );

  const ActionRenderer = useCallback(
    (params) => {
      const row = params.data;
      const isPosting = row.isPosting === 1;
      const totalUnpaid = row.totalUnpaid === 0 ? false : true;
      const bookMarkValue = row.isBookmark;
      const bookmark = row.isBookmark === 1 ? "btn-success" : "btn-outline-success";

      return (
        <Fragment>
          {!isPosting && (
            <Button
              button={`${bookmark} btn-sm me-1`}
              icon="fa fa-bookmark"
              onClick={() => handleToggleBookmark(row.id, bookmark, bookMarkValue)}
            />
          )}

          {totalUnpaid && (
            <Button
              button="btn-outline-warning btn-sm"
              icon="fa fa-wrench text-warning"
              onClick={() => detailHandler(row.id, row.workerName, row)}
            />
          )}
        </Fragment>
      );
    },
    [handleToggleBookmark, detailHandler]
  );

  const closeAllModals = useCallback(() => {
    setModalPosting(false);
    setModalDetail(false);
    setModalImage(false);
  }, []);

  const closeImageModals = useCallback(() => {
    setModalImage(false);
  }, []);

  const handleChangePage = useCallback(({ selected }) => {
    setPageNumber(selected + 1);
  }, []);

  const handleSortOrder = (payload) => {
    setPayloadSort(payload);
  };

  const colDefs = useMemo(() => ColumnCutRecap(ActionRenderer, handleSortOrder, payloadSort), [ActionRenderer, payloadSort]);

  return (
    <div className="w-100 d-flex flex-column gap-3">
      {modalPosting && <Posting activeModal={modalPosting} closeModal={closeAllModals} onConfirm={handlePost} />}
      {modalDetail && (
        <Detail activeModal={modalDetail} closeModal={closeAllModals} data={dataDetail} imageHandler={imageHandler} />
      )}
      {modalImage && <ImageEvidance activeModal={modalImage} closeModal={closeImageModals} data={image} />}

      <ActionRecap
        rangeDate={startDate && endDate ? [moment(startDate).toDate(), moment(endDate).toDate()] : []}
        onDateChange={handleDateChange}
        onFilter={handleFilter}
        onPost={handOpenModalPost}
        onSearch={handleSearch}
        loadingFilter={loadingFilter}
        loadingPost={loadingPost}
      />

      <div
        className="ag-theme-alpine w-100"
        style={{
          height: 50 * rowData.length + 50 + "px",
          maxHeight: 50 * rowData.length + 50 + "px",
          minHeight: "250px",
          overflow: "auto",
        }}
      >
        <AgGridReact
          rowData={loading ? [] : rowData}
          columnDefs={colDefs}
          domLayout="normal"
          theme="legacy"
          rowHeight={50}
          headerHeight={50}
          animateRows={true}
          enableCellTextSelection={true}
          sideBar={false}
          defaultColDef={{ sortable: false, filter: false, resizable: true }}
          overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Memuat data...</span>'}
          overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">Tidak ada data</span>'}
          getRowStyle={(params) => {
            switch (params.data?.isPosting) {
              case 1:
                return { color: "#7041c1" };
              case 0:
                return { color: "#000000" };
              default:
                return { backgroundColor: "transparent" };
            }
          }}
        />
      </div>
      {totalPage > 0 && <Pagination pageCount={totalPage} forcePage={pageNumber - 1} changePage={handleChangePage} />}
    </div>
  );
}

export default SalaryCutCashReceipt;
